import { apiCall, apiStream } from '@/lib/api';
import {
  createInfiniteQuery,
  createMutation,
  createQuery,
  useQueryClient,
} from '@tanstack/solid-query';
import type {
  CreateReleaseDraftBody,
  DistributeReleaseBody,
  PutReleaseRequirementsBody,
  ReleaseEnvironment,
  ReleaseStatus,
  UpdateReleaseDraftBody,
} from '@uchat-team-management/shared';
import { createEffect, createMemo, onCleanup } from 'solid-js';
import {
  closeReleasePreview,
  compareReleases,
  confirmRelease,
  createReleaseDraft,
  deleteReleaseDraft,
  distributeRelease,
  getRelease,
  getReleaseChangelog,
  getReleaseCommit,
  getReleasePreview,
  getWorkflowRunLogs,
  listGitBranches,
  listGitCommits,
  listGitPulls,
  listGitTags,
  listGitWorkflows,
  listReleaseBaselines,
  listReleaseVersions,
  listReleases,
  promoteRelease,
  publishRelease,
  putReleaseRequirements,
  reconcileRelease,
  refreshReleaseRef,
  refreshReleaseTasks,
  releaseEventsStream,
  retryWorkflowRun,
  suggestVersion,
  unwatchRelease,
  updateReleaseDraft,
  watchRelease,
} from './api';
import { parseSse } from './sse';

export interface ReleasesFilters {
  productId?: string;
  environment?: ReleaseEnvironment;
  status?: ReleaseStatus;
  /** Matches version fragments and description text. */
  q?: string;
}

export const releasesKey = (filters: ReleasesFilters) => ['releases', filters] as const;
export const releaseKey = (id: string) => ['releases', id] as const;

/** Safety-net poll while running; live updates arrive over SSE (useReleaseEvents). */
const RUNNING_REFETCH_MS = 30_000;
const STREAM_RETRY_MS = 5000;

/** Running builds and publishing deployments update live. */
const isLive = (status: ReleaseStatus | undefined): boolean =>
  status === 'running' || status === 'publishing';

/** Poll only while the release is running or publishing (workflow/deploy monitoring). */
export const runningRefetchInterval = (status: ReleaseStatus | undefined): number | false =>
  isLive(status) ? RUNNING_REFETCH_MS : false;

export const useReleases = (filters: () => ReleasesFilters) =>
  createQuery(() => ({
    queryKey: releasesKey(filters()),
    queryFn: () => apiCall(listReleases, { query: filters() }),
  }));

// UCH-447: paged versions table — one infinite-query page per page number.
// The 'list' segment keeps the key clear of releaseKey(id) = ['releases', id].
export const useReleasesList = (filters: () => ReleasesFilters) =>
  createInfiniteQuery(() => ({
    queryKey: ['releases', 'list', filters()] as const,
    queryFn: ({ pageParam }) =>
      apiCall(listReleases, { query: { ...filters(), cursor: pageParam || undefined } }),
    initialPageParam: '',
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
  }));

export const useReleaseBaselines = (releaseId: () => string, q: () => string) =>
  createInfiniteQuery(() => ({
    queryKey: ['releases', releaseId(), 'baselines', q()] as const,
    queryFn: ({ pageParam }) =>
      apiCall(listReleaseBaselines, {
        params: { releaseId: releaseId() },
        query: { q: q() || undefined, offset: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.meta.nextOffset ?? undefined,
  }));

/**
 * While `running()`, holds an SSE stream open and refetches the release on each
 * `changed` signal; reconnects after STREAM_RETRY_MS if it drops.
 */
const useReleaseEvents = (releaseId: () => string, running: () => boolean) => {
  const qc = useQueryClient();
  createEffect(() => {
    if (!running()) return;
    const id = releaseId();
    const ctrl = new AbortController();
    onCleanup(() => ctrl.abort());
    void (async () => {
      while (!ctrl.signal.aborted) {
        try {
          const res = await apiStream(releaseEventsStream, {
            params: { releaseId: id },
            signal: ctrl.signal,
          });
          const reader = res.ok ? res.body?.getReader() : undefined;
          const decoder = new TextDecoder();
          let buffer = '';
          for (let r = await reader?.read(); r && !r.done; r = await reader?.read()) {
            const parsed = parseSse(buffer + decoder.decode(r.value, { stream: true }));
            buffer = parsed.rest;
            if (parsed.events.includes('changed'))
              void qc.invalidateQueries({ queryKey: releaseKey(id), exact: true });
          }
        } catch {
          // Network drop or abort — retry below unless aborted; the 30s poll covers the gap.
        }
        await new Promise((resolve) => setTimeout(resolve, STREAM_RETRY_MS));
      }
    })();
  });
};

export const useRelease = (releaseId: () => string) => {
  const query = createQuery(() => ({
    enabled: releaseId() !== '',
    queryKey: releaseKey(releaseId()),
    queryFn: () => apiCall(getRelease, { params: { releaseId: releaseId() } }),
    refetchInterval: (q) => runningRefetchInterval(q.state.data?.data.status),
  }));
  // Memo: refetches swap `data` but must not reopen the stream unless running flips.
  useReleaseEvents(
    releaseId,
    createMemo(() => isLive(query.data?.data.status)),
  );
  return query;
};

export const useSuggestVersion = (input: () => { productId: string; environment: string } | null) =>
  createQuery(() => ({
    enabled: input() !== null,
    queryKey: ['releases', 'suggest', input()] as const,
    queryFn: () =>
      apiCall(suggestVersion, {
        query: input() as { productId: string; environment: ReleaseEnvironment },
      }),
  }));

const invalidateReleases = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ['releases'] });

export const useCreateDraft = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: CreateReleaseDraftBody) => apiCall(createReleaseDraft, { body }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

export const useUpdateDraft = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: UpdateReleaseDraftBody) =>
      apiCall(updateReleaseDraft, { params: { releaseId }, body }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

export const useDeleteDraft = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(deleteReleaseDraft, { params: { releaseId } }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

export const usePromoteRelease = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(promoteRelease, { params: { releaseId } }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

export const useConfirmRelease = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(confirmRelease, { params: { releaseId } }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

export const useReleaseChangelog = (
  input: () => {
    releaseId: string;
    baseReleaseId?: string;
    against: 'baseline' | 'published';
  },
) =>
  createQuery(() => ({
    queryKey: ['releases', input().releaseId, 'changelog', input()] as const,
    queryFn: () =>
      apiCall(getReleaseChangelog, {
        params: { releaseId: input().releaseId },
        query: { baseReleaseId: input().baseReleaseId, against: input().against },
      }),
  }));

// --- GitHub browse (UCH-239 ref picker) ---

export const useGitPulls = (productId: () => string) =>
  createQuery(() => ({
    enabled: productId() !== '',
    queryKey: ['git', productId(), 'pulls'] as const,
    queryFn: () => apiCall(listGitPulls, { params: { productId: productId() } }),
  }));

export const useGitCommits = (input: () => { productId: string; ref?: string; sha?: string }) =>
  createQuery(() => ({
    enabled: input().productId !== '',
    queryKey: ['git', input().productId, 'commits', input().ref ?? '', input().sha ?? ''] as const,
    queryFn: () =>
      apiCall(listGitCommits, {
        params: { productId: input().productId },
        query: { ref: input().ref, sha: input().sha },
      }),
  }));

/** A release's pinned commit via the view-level route ('' disables it). */
export const useReleaseCommit = (releaseId: () => string) =>
  createQuery(() => ({
    enabled: releaseId() !== '',
    queryKey: ['releases', releaseId(), 'commit'] as const,
    queryFn: () => apiCall(getReleaseCommit, { params: { releaseId: releaseId() } }),
  }));

export const useGitBranches = (input: () => { productId: string; q?: string }) =>
  createQuery(() => ({
    enabled: input().productId !== '',
    queryKey: ['git', input().productId, 'branches', input().q ?? ''] as const,
    queryFn: () =>
      apiCall(listGitBranches, {
        params: { productId: input().productId },
        query: { q: input().q },
      }),
  }));

/** Workflow files at a ref — enabled only once a ref/SHA is pinned. */
export const useGitWorkflows = (input: () => { productId: string; ref: string }) =>
  createQuery(() => ({
    enabled: input().productId !== '' && input().ref !== '',
    queryKey: ['git', input().productId, 'workflows', input().ref] as const,
    queryFn: () =>
      apiCall(listGitWorkflows, {
        params: { productId: input().productId },
        query: { ref: input().ref },
      }),
  }));

export const useGitTags = (input: () => { productId: string; q?: string }) =>
  createQuery(() => ({
    enabled: input().productId !== '',
    queryKey: ['git', input().productId, 'tags', input().q ?? ''] as const,
    queryFn: () =>
      apiCall(listGitTags, { params: { productId: input().productId }, query: { q: input().q } }),
  }));

export const useReleaseVersions = (
  input: () => { productId?: string; environment?: ReleaseEnvironment } | null,
) =>
  createQuery(() => ({
    enabled: input() !== null,
    queryKey: ['releases', 'versions', input()] as const,
    queryFn: () =>
      apiCall(listReleaseVersions, {
        query: input() as { productId?: string; environment?: ReleaseEnvironment },
      }),
  }));

export const useCompareReleases = (
  input: () => { baseReleaseId: string; headReleaseId: string } | null,
) =>
  createQuery(() => ({
    enabled: input() !== null,
    queryKey: ['releases', 'compare', input()] as const,
    queryFn: () =>
      apiCall(compareReleases, {
        query: input() as { baseReleaseId: string; headReleaseId: string },
      }),
  }));

export const useSetRequirements = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: PutReleaseRequirementsBody) =>
      apiCall(putReleaseRequirements, { params: { releaseId }, body }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

export const useRefreshRef = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(refreshReleaseRef, { params: { releaseId } }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

/** Force re-resolve ClickUp metadata into the server cache (UCH-273). */
export const useRefreshReleaseTasks = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (query: { against: 'baseline' | 'published' }) =>
      apiCall(refreshReleaseTasks, { params: { releaseId }, query }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases', releaseId, 'changelog'] }),
  }));
};

export const useReconcileRelease = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(reconcileRelease, { params: { releaseId } }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

/** Toggle Slack DM opt-in for one release (UCH-806); detail carries `watching`. */
export const useWatchRelease = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (watch: boolean) =>
      apiCall(watch ? watchRelease : unwatchRelease, { params: { releaseId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: releaseKey(releaseId) }),
  }));
};

/** Re-run the failed jobs of one workflow run; the release returns to running. */
export const useRetryWorkflowRun = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (runId: string) => apiCall(retryWorkflowRun, { params: { releaseId, runId } }),
    onSuccess: () => invalidateReleases(qc),
  }));
};

/** Presigned URL of an attempt's archived full-logs zip. */
export const fetchRunLogsUrl = async (
  releaseId: string,
  runId: string,
  attemptNumber: number,
): Promise<string> => {
  const res = await apiCall(getWorkflowRunLogs, { params: { releaseId, runId, attemptNumber } });
  return res.data.url;
};

/** No invalidation: distributing to FAD testers changes nothing in our DB (UCH-418). */
export const useDistributeRelease = (releaseId: string) =>
  createMutation(() => ({
    mutationFn: (body: DistributeReleaseBody) =>
      apiCall(distributeRelease, { params: { releaseId }, body }),
  }));

const previewKey = (releaseId: string) => ['releases', releaseId, 'preview'] as const;

/**
 * Live state of a release-web build's Cloudflare Pages preview. Publish removes
 * the preview in the background, so a published release re-checks every 5s
 * while it still reads active.
 */
export const useReleasePreview = (releaseId: () => string, published: () => boolean) =>
  createQuery(() => ({
    queryKey: previewKey(releaseId()),
    queryFn: () => apiCall(getReleasePreview, { params: { releaseId: releaseId() } }),
    retry: false,
    refetchInterval: (query) =>
      published() && query.state.data?.data.state === 'active' ? 5_000 : false,
  }));

export const useCloseReleasePreview = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(closeReleasePreview, { params: { releaseId } }),
    onSuccess: (res) => qc.setQueryData(previewKey(releaseId), res),
  }));
};

export const usePublishRelease = (releaseId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(publishRelease, { params: { releaseId } }),
    onSuccess: () => invalidateReleases(qc),
  }));
};
